// SPEC-392 段B — visual review loop policy: intent gate, refine budget, refine-instruction builder.
//
// The canvas renders in the browser (Konva) where the worker can't see it, so visual checking
// round-trips "frontend screenshot → upload → worker critique". This module decides WHICH turns
// enter that loop and how many refine rounds they get. Pure functions only — orchestration lives
// in App.tsx.
//
// Intent heuristics mirror worker/src/agent.ts (looksLikeNewDesign / looksLikeQuestion /
// looksLikeEditRequest) — the web bundle can't import worker code, so keep the two in sync by hand.

import type { AgentResult, CanvasState } from '../types';
import type { CritiqueIssue, CritiqueResult } from '../api';
import type { ReviewPhase } from './reviewPhase';

// ===== intent heuristics (mirror of worker/src/agent.ts) =====

const EDIT_MARKERS = /(改|加|换|删|移|调|去掉|换成|变成|放大|缩小|颜色|位置|重新排|保留)/;
const QUESTION_MARKERS = /[？?]|什么|怎么|如何|为什么|哪[个里种]|吗$|呢$/;
const NEGATED_EDIT = /(别改|不要改|不用改|别动|不要动|不用动|保持原样|不需要改)/;

export function looksLikeNewDesign(msg: string): boolean {
  return !EDIT_MARKERS.test(msg);
}

export function looksLikeQuestion(msg: string): boolean {
  return QUESTION_MARKERS.test(msg);
}

export function looksLikeEditRequest(msg: string, hasObjects: boolean): boolean {
  return hasObjects && EDIT_MARKERS.test(msg) && !NEGATED_EDIT.test(msg);
}

// ===== change-magnitude thresholds =====
// Criteria (判据):
// - A complete new poster lands as ≥3 objects (full-bleed visual + title + info text); UI mockups
//   15-30 objects. So ≥5 added objects OR any new generated image OR ≥8 ops ⇒ new design / big
//   rework — worth a visual review.
// - Small edits touch 1-2 objects (≤3 ops, no new generation) — they skip the loop entirely and
//   behave exactly like today (直通).
// - An edit-marked message with a big diff ("重新排版整页") still enters the loop: the marker says
//   "edit", the diff says "big" — diff wins.
const BIG_ADDED_OBJECTS = 5;
const BIG_OPS = 8;

export function isBigChange(before: CanvasState, after: CanvasState, result: AgentResult): boolean {
  const addedObjects = after.objects.length - before.objects.length;
  const genJobs = result.jobs.filter(j => j.kind === 'gen').length;
  return addedObjects >= BIG_ADDED_OBJECTS || genJobs >= 1 || result.ops.length >= BIG_OPS;
}

/**
 * Intent gate: true = this turn enters the screenshot→critique→refine loop.
 * Pass-through (false) keeps byte-for-byte today's behavior for small edits, Q&A, failed turns
 * and turns that changed nothing on the canvas.
 */
export function shouldEnterLoop(
  message: string,
  before: CanvasState,
  after: CanvasState,
  result: AgentResult,
  /** SPEC-426 M2 A.2 (修复轮): explicit review phase — requireReviewPhase(isBigChange, ops).
   * 'visual' = 该轮必审：过了 error/question/no-change 守卫后强制 true（验收 4c）;
   * 'skip' = 审查非强制（≠禁止！上轮实现把非强制当禁止被 request_changes 打回）——走原
   * heuristics，新设计意图小 diff 仍进 loop（行为不回退）。Optional — omitted keeps the
   * pre-M2 signature and behavior byte-identical. */
  phase?: ReviewPhase
): boolean {
  if (result.error) return false; // failed turn → retry bubble path, no review
  if (looksLikeQuestion(message)) return false; // Q&A / consultation never loops
  const changed = result.ops.length > 0 || result.jobs.length > 0;
  if (!changed) return false; // nothing landed on the canvas → nothing to screenshot
  // SPEC-426 M2 A.2 (修复轮): big-change 轮必审 — 守卫之后强制进 loop（screenshot+critique 相位必落盘）。
  if (phase === 'visual') return true;
  // Edit intent + small diff = 直通; big diff overrides the edit marker (see thresholds above).
  if (looksLikeEditRequest(message, before.objects.length > 0) && !isBigChange(before, after, result)) return false;
  return true;
}

// ===== refine budget =====
// SPEC-427 C: 建设轮与修正轮预算分池 — 本文件的 REFINE_BUDGET_* 全部属于「修正池」（视觉审查
// refine 轮上限，web 侧唯一消耗点）；「建设池」在 worker 侧（BUILD_MAX_ROUNDS + 分段 opsBudget，
// 仅复杂大建轮），两池互不消耗 —— 建设预算不再被修正轮吃掉（取证：「新增 1 个对象，修改 64 处」）。
// Default 2 refine rounds; structural problems (layout / stacking / overflow class — the kind a
// single tweak rarely fixes cleanly) get at most 3. Budget locks at the first verdict.
// (SPEC-410 F4: raised 1→2 / 2→3 — one extra round trades ≈+20-40s latency for real convergence.)
// SPEC-426 M2 A.2: budgets are phase-aware — 'grounding' keeps the historical constants;
// 'visual' gets independently named constants (same values today) so M3 can retune one phase
// without touching the other.
export const REFINE_BUDGET_DEFAULT = 2;
export const REFINE_BUDGET_STRUCTURAL = 3;
export const REFINE_BUDGET_VISUAL_DEFAULT = 2;
export const REFINE_BUDGET_VISUAL_STRUCTURAL = 3;

const STRUCTURAL_RE = /(布局|排版|层级|层次|遮挡|挡住|对齐|对不齐|越界|重叠|超出|溢出|结构|位置不对|z序)/;

export function hasStructuralIssues(issues: CritiqueIssue[]): boolean {
  return issues.some(i => i.severity === 'hard' && STRUCTURAL_RE.test(i.desc));
}

export function refineBudget(issues: CritiqueIssue[], phase: ReviewPhase = 'visual'): number {
  const structural = hasStructuralIssues(issues);
  if (phase === 'grounding') return structural ? REFINE_BUDGET_STRUCTURAL : REFINE_BUDGET_DEFAULT;
  return structural ? REFINE_BUDGET_VISUAL_STRUCTURAL : REFINE_BUDGET_VISUAL_DEFAULT;
}

/** SPEC-462 M4: 再归因注入条目（收敛再诊断）— 连续 2 修复轮仍存的 stuck finding 不再进
 *「逐项修复」清单（禁止第三轮重复同款 hint），改入尾部再归因段：历史轮 desc + 已试
 * ops 摘要 + 换根因/降级要求。 */
export interface ReattributionInfo {
  /** stuck finding 的当轮 issue 实例（residualFingerprint 身份锚定）。 */
  issue: CritiqueIssue;
  /** 历史轮 desc（轮次序 — updateFindingRounds 账本 descs）。 */
  history: string[];
  /** 已试修复轮 ops 确定性摘要（summarizeTriedOps 产物）。 */
  triedOps?: string;
}

/** Feed issues back to the planner as a correction turn: hard issues first (must fix), then soft
 * issues tagged secondary — fixed opportunistically in the same round. Soft issues alone never
 * trigger a round (the loop only refines while hard issues exist). (SPEC-410 F4)
 * SPEC-426 M2 A.3 (修复轮): optional ctx injects the convergence-guard lists — regressed items
 * (指纹重现 → 务必重修) and fixed items (不得重新引入). Omitted ctx keeps the pre-M2 instruction
 * byte-identical (旧签名兼容).
 * SPEC-462 M4 (收敛再诊断): optional ctx.reattribute switches stuck findings (连续 2 修复轮
 * 仍存) into the re-attribution section — they leave the 逐项修复/务必重修 lists; absent
 * reattribute → output byte-identical with the current version. */
export function buildRefineInstruction(
  issues: CritiqueIssue[],
  ctx?: { fixed?: CritiqueIssue[]; regressed?: CritiqueIssue[]; reattribute?: ReattributionInfo[] }
): string {
  // SPEC-460 M3-2: 逐对象锚定 — issue 带 object 图层名且 desc 未点名时补锚定注（旧输入无
  // object 键 → 输出逐字节一致，spec426:1008/1035 pin 不受影响）。
  const fmt = (i: CritiqueIssue, n: number) => {
    const obj = (i as PlacementIssue).object;
    const objPart = obj && !i.desc.includes(obj) ? `（对象：${obj}）` : '';
    return `${n + 1}. ${i.desc}${i.region ? `（位置：${i.region}）` : ''}${objPart}`;
  };
  // SPEC-462 M4: stuck finding 不进动作清单（禁止重复同款 hint）；无 reattribute → notStuck
  // 恒真，各段输出与现版逐字节一致（spec426:1008/1035 pin）。
  const reattr = ctx?.reattribute ?? [];
  const reattrFps = reattr.length > 0 ? new Set(reattr.map(r => residualFingerprint(r.issue))) : null;
  const notStuck = (i: CritiqueIssue): boolean => reattrFps === null || !reattrFps.has(residualFingerprint(i));
  const hard = issues.filter(i => i.severity === 'hard' && notStuck(i));
  const soft = issues.filter(i => i.severity === 'soft');
  const hardLines = hard.map(fmt).join('\n');
  const softPart = soft.length
    ? `\n次优先，同轮顺手修：\n${soft.map(fmt).join('\n')}`
    : '';
  const regressedList = (ctx?.regressed ?? []).filter(notStuck); // stuck 不进「务必重修」（与再归因要求矛盾）
  const regressedPart = regressedList.length
    ? `\n以下问题上一轮已修复但本轮回归（指纹重现），务必重修：\n${regressedList.map(fmt).join('\n')}`
    : '';
  const fixedPart = ctx?.fixed?.length
    ? `\n上一轮已修复、不得重新引入：\n${ctx.fixed.map(fmt).join('\n')}`
    : '';
  const reattrPart = reattr.length
    ? `\n以下问题已连续 2 轮以上修复后仍存在——同款修复动作已被证明无效，禁止再次执行；请重新归因：换不同的根因归属（真实缺陷可能在别的容器/图层，逐项核对几何证据），或说明降级理由（为何可不修）：\n${reattr.map((r, n) => `${n + 1}. 持续 finding：${r.history.join(' → ')}${r.triedOps ? `\n   已试修复动作：${r.triedOps}` : ''}`).join('\n')}`
    : '';
  return `【视觉审查修正】对渲染截图复查后发现以下问题，请逐项修复：\n${hardLines}${softPart}${regressedPart}${fixedPart}${reattrPart}\n请调用工具针对性修正，只处理上述问题，不要重做整稿。`;
}

// ===== SPEC-426 M2 A.3 (修复轮) — 收敛与回归守卫 =====

/** Stable fingerprint of an issue: region+desc normalized (trim / lowercase / punctuation and
 * whitespace stripped), so cosmetically different reviewer wordings still match across rounds. */
export function issueFingerprint(issue: CritiqueIssue): string {
  const norm = (s?: string) => (s ?? '').trim().toLowerCase().replace(/[\s\p{P}\p{S}]+/gu, '');
  return `${norm(issue.region)}|${norm(issue.desc)}`;
}

/**
 * Cross-round convergence guard (spec §A.3): compare the previous verdict's issues with this
 * round's —
 * - fixed: 上轮存在、本轮消失 = 已修（refine 指令携带「不得重新引入」清单）;
 * - regressed: 本轮 hard 中指纹在上一轮 verdict 已出现过 — 上一轮 refine 点名要修的问题
 *   重现（指纹重现）;
 * - regression: 指纹重现 或 hard 总数上升（取证案例 hard 5→3→2→4：末轮回归变差而旧实现零标记）.
 * Callers invoke this only once a previous verdict exists (first round → no guard, no marking).
 */
export function detectRegression(
  prevIssues: CritiqueIssue[],
  nextIssues: CritiqueIssue[]
): { regression: boolean; regressed: CritiqueIssue[]; fixed: CritiqueIssue[] } {
  const prevHard = prevIssues.filter(i => i.severity === 'hard');
  const nextHard = nextIssues.filter(i => i.severity === 'hard');
  const prevFps = new Set(prevHard.map(issueFingerprint));
  const nextFps = new Set(nextHard.map(issueFingerprint));
  const regressed = nextHard.filter(i => prevFps.has(issueFingerprint(i)));
  const fixed = prevHard.filter(i => !nextFps.has(issueFingerprint(i)));
  return { regression: regressed.length > 0 || nextHard.length > prevHard.length, regressed, fixed };
}

// ===== SPEC-463 M2.2/M2.3 — 终局消息聚合（单一 exhausted 报告 + residual 去重）=====
// 生产取证（doc-pmutgxupb3gz0 msg3）：终局泡 gate 文案开头 + 双份「Refine budget exhausted」
//（8 issue(s) done 泡 + 12 issue(s) 终局补审泡 — CHAT_APPEND 连续 assistant 同泡合并把聚合缺陷
// 放大成单条消息）；residual 12 条中 4 条为确定性审计 vs vision 双通道同源重复 → 唯一 finding 实为 8。

/** 预算耗尽诚实披露标记 — chat.loopUnresolved zh/en 文案稳定子串（worker agent.ts
 * LOOP_UNRESOLVED_MARKERS 同源镜像；i18n 键文案交叉 pin 防漂移，spec461m3 M3.1 先例）。 */
export const LOOP_EXHAUSTED_MARKERS: readonly string[] = ['修正轮数上限已用尽', 'Refine budget exhausted'];

/** SPEC-463 M2.3: 终局 residual finding 指纹 — 「对象名 + finding 类型 + 关键证据」三元（spec 定义）。
 * 比 issueFingerprint（region+desc 全文归一）更聚合：同一缺陷由确定性审计与 vision 双通道分别
 * 描述（措辞不同、region 不同/缺失）仍同指纹（取证：「product_feed」孤儿压盖 ×2、txt_sales_1/2
 * 出画布 ×2 各归并）。提取：finding 类型 = desc 首个冒号前的类别前缀；对象名/关键证据 = desc 中
 * 第一/二个引号内图层名。提不出 → 退回 issueFingerprint（更细粒度，不误并）。 */
export function residualFingerprint(issue: CritiqueIssue): string {
  const norm = (s?: string) => (s ?? '').trim().toLowerCase().replace(/[\s\p{P}\p{S}]+/gu, '');
  const desc = issue.desc ?? '';
  const ci = desc.search(/[：:]/);
  const category = ci > 0 ? norm(desc.slice(0, ci)) : '';
  const quotes = [...desc.matchAll(/[“”「『‘’"]([^“”」』‘’"]{1,80})[“”」』‘’"]/g)].map(m => norm(m[1]));
  if (!category || quotes.length === 0) return issueFingerprint(issue);
  return `${category}|${quotes[0]}|${quotes[1] ?? ''}`;
}

/** SPEC-463 M2.3: hard-issue 按 residualFingerprint 去重（保首现顺序）— residual 计数/入数组前都过此层。 */
export function dedupHardIssues(issues: CritiqueIssue[]): CritiqueIssue[] {
  const seen = new Set<string>();
  const out: CritiqueIssue[] = [];
  for (const i of issues) {
    if (!i || i.severity !== 'hard') continue;
    const fp = residualFingerprint(i);
    if (seen.has(fp)) continue;
    seen.add(fp);
    out.push(i);
  }
  return out;
}

/** Residual list for the done bubble (budget exhausted with hard issues left): hard-only copies
 * carrying desc+region+severity — severity keeps buildRefineInstruction(residual) working for the
 * one-click continue-refine path.
 * SPEC-463 M2.3: 入清单前按 finding 指纹去重（dedupHardIssues）— 双通道同源重复不再虚高计数；
 * 无重复输入的旧调用方结果逐条不变（保序、保首现）。 */
export function buildResidual(issues: CritiqueIssue[]): CritiqueIssue[] {
  return dedupHardIssues(issues)
    .map(i => ({ desc: i.desc, severity: i.severity, ...(i.region ? { region: i.region } : {}) }));
}

/** SPEC-463 M2.2: 终局补审单一报告合并（纯函数；lead 由调用方注入 — i18n 键复用
 * chat.loopUnresolved，别造新键）。prev = 最近一条 assistant 泡（可能携 done 泡落盘的 residual）：
 * 已含 exhausted 披露 → merged=true，新 text = lead(合并去重计数) + 原 lead 之后的尾部
 *（summaries 等保留），原 lead 之前的文本（如误混入的 gate 文案）随改写自然脱落；
 * residual = 合并去重后清单。无 exhausted 前提 → 首次报告（residual 同样去重）。 */
export function mergeReauditReport(
  prev: { text: string; residual?: CritiqueIssue[] } | null | undefined,
  hard: CritiqueIssue[],
  lead: (n: number) => string,
): { merged: boolean; text: string; residual: CritiqueIssue[] } {
  const exhaustedBefore = !!prev && LOOP_EXHAUSTED_MARKERS.some(m => prev.text.includes(m));
  if (!exhaustedBefore || !prev) {
    const residual = buildResidual(hard);
    return { merged: false, text: lead(residual.length), residual };
  }
  const residual = buildResidual([...(prev.residual ?? []), ...hard]);
  const oldLead = lead((prev.residual ?? []).length);
  const idx = prev.text.indexOf(oldLead);
  const suffix = idx >= 0 ? prev.text.slice(idx + oldLead.length) : '';
  return { merged: true, text: lead(residual.length) + suffix, residual };
}

// ===== SPEC-427 A — frozen-verdict 硬熔断 =====
// 生产取证（SPEC-427, doc:dale pmu4t8yi）：verdict 冻结 hard=2 连续 8 轮，A.3 指令级注入
//（「不得重新引入」）治不了 frozen loop——相同修正反复提交、预算反复耗尽。判定升级：
// 连续 2 轮 critique 的 hard 指纹集合（issueFingerprint）完全一致 → FROZEN，refine 循环硬停。

/** Hard-issue fingerprint set of a verdict (issueFingerprint per hard issue). */
export function hardFingerprintSet(issues: CritiqueIssue[]): Set<string> {
  return new Set(issues.filter(i => i.severity === 'hard').map(issueFingerprint));
}

/** True when both verdicts carry an identical hard-issue fingerprint set (order-insensitive). */
export function sameHardFingerprints(prev: CritiqueIssue[], next: CritiqueIssue[]): boolean {
  const a = hardFingerprintSet(prev);
  const b = hardFingerprintSet(next);
  if (a.size !== b.size) return false;
  for (const fp of a) if (!b.has(fp)) return false;
  return true;
}

/** FROZEN 判定（spec §A）：连续两轮 critique 的 hard 指纹集合一致且非空 → 文字修正无效。
 * Empty-on-empty (all fixed) is NOT frozen — that's convergence, handled by the pass break. */
export function isVerdictFrozen(prev: CritiqueIssue[], next: CritiqueIssue[]): boolean {
  return hardFingerprintSet(next).size > 0 && sameHardFingerprints(prev, next);
}

// ===== SPEC-462 M4 — 收敛再诊断（E5 根治：冻结 finding 的修复轮重复同款无效 op）=====
// 生产取证（SPEC-462 E5）：同 finding 连续冻结时，修复轮重复同款无效 op — SPEC-427 熔断
// 只停机不复归因，停机前从未给模型「换根因」的机会。M4：同一 finding（residualFingerprint —
// det+vision 双通道同一缺陷聚合；issueFingerprint 全文归一会因 desc 数值微漂误分裂）连续
// 2 个修复轮仍存（= 3 个 verdict 出现）→ 第 3 轮 refine 指令强制切再归因形态：历史轮
// desc + 已试 ops 摘要 +「换根因归属或说明降级理由，禁止重复同款修复动作」。
// 时序红线：再归因先于 isVerdictFrozen 熔断生效 — frozen 命中的 verdict 若含尚未给过再归因
// 轮的 stuck finding，先发一轮再归因（每指纹一次），下一轮仍冻结再照走熔断；SPEC-427 熔断
// 语义与常量零改动。

/** 再归因触发阈值：同一 finding 连续出现的 verdict 数 = 3（首见 + 2 个修复轮后仍存）→ 第 3 轮强制再归因。 */
export const REATTRIBUTE_AFTER_VERDICTS = 3;

/** 跨轮 finding 账本条目：rounds = 连续出现的 verdict 轮数；descs = 历史轮 desc（轮次序，相邻去重）。 */
export interface FindingRoundEntry { rounds: number; descs: string[]; }
/** 跨轮 finding 账本：residualFingerprint → 条目。 */
export type FindingRounds = Map<string, FindingRoundEntry>;

/** 每轮 verdict 后记账（纯函数，可单测）：hard issues 按 residualFingerprint 去重后 —
 * 本轮在场 → rounds+1 + desc 追加（与上轮逐字节相同不重复追加）；本轮缺席 → 条目移除
 *（连续性中断，再出现从 1 重新计）。 */
export function updateFindingRounds(prev: FindingRounds | null | undefined, issues: CritiqueIssue[]): FindingRounds {
  const next: FindingRounds = new Map();
  for (const i of dedupHardIssues(issues)) {
    const fp = residualFingerprint(i);
    const old = prev?.get(fp);
    const descs = old ? [...old.descs] : [];
    if (descs.length === 0 || descs[descs.length - 1] !== i.desc) descs.push(i.desc);
    next.set(fp, { rounds: (old?.rounds ?? 0) + 1, descs });
  }
  return next;
}

/** 本轮应强制再归因的 stuck finding。 */
export interface StuckFinding { fingerprint: string; issue: CritiqueIssue; rounds: number; descs: string[]; }

/** stuck 清单（纯函数）：当轮 hard issues（去重后）中连续轮数 ≥ threshold 者（缺省
 * REATTRIBUTE_AFTER_VERDICTS），按出现序输出。 */
export function stuckFindings(fr: FindingRounds, issues: CritiqueIssue[], threshold: number = REATTRIBUTE_AFTER_VERDICTS): StuckFinding[] {
  const out: StuckFinding[] = [];
  for (const i of dedupHardIssues(issues)) {
    const fp = residualFingerprint(i);
    const e = fr.get(fp);
    if (e && e.rounds >= threshold) out.push({ fingerprint: fp, issue: i, rounds: e.rounds, descs: e.descs });
  }
  return out;
}

/** 时序红线判定（纯函数，可单测）：frozen 熔断命中但 verdict 仍含尚未给过再归因轮的
 * stuck finding（指纹不在 alreadyReattributed）→ true = 推迟熔断先发再归因轮（每指纹
 * 一次）；否则 false = 熔断照常生效（SPEC-427 语义不变）。 */
export function reattributionDefersFreeze(stuck: readonly StuckFinding[], alreadyReattributed: ReadonlySet<string>): boolean {
  return stuck.some(s => !alreadyReattributed.has(s.fingerprint));
}

/** 已试修复轮 ops 确定性摘要（E5 再归因素材 — ops/图像任务计数 + 空轮标记，零 LLM）。 */
export function summarizeTriedOps(rounds: readonly { ops: number; jobs: number }[]): string {
  if (rounds.length === 0) return '无';
  return rounds
    .map((r, i) => `第${i + 1}轮${r.ops + r.jobs === 0 ? '空转（零变更落地）' : `${r.ops} ops/${r.jobs} 图像任务`}`)
    .join('；');
}

// ===== SPEC-460 M3 — 审计/vision 汇总仲裁 =====
// 生产取证（doc-dale pmuqnq4q7dg8u，spec 证据 3）：末轮 vision 判词「四个 tab 视觉均为 icon 上、
// text 下……头像正常」与 raw 画布（tab_discover 子序翻转 + avatar 压扁 32×18）完全相反，
// refine 循环采信误报 → 真实缺陷从未被修，hard=5 未解决出厂（证据 4）。仲裁规则（spec M3-1）：
// 确定性审计 hard 不可被 vision「pass」覆盖 — 两通道任一有 hard 即 fail。数据源 =
// /api/critique 响应客观数据段 placement（worker index.ts placementIssues 产出，
// icon_size_findings 先例同段暴露）。

/** /api/critique 客观数据段条目（CritiqueIssue 同形 + object 锚定 — 确定性通道天然逐对象）。 */
export interface PlacementIssue extends CritiqueIssue {
  /** 锚定对象图层名（M3-2 逐对象锚定）。 */
  object?: string;
}

/**
 * SPEC-462 M3: worker /api/critique 响应 grounding 段的 resolve 判定 — 可测量主张 vs 区域
 * 几何 facts 的确定性仲裁（worker facts.ts arbitrateClaims 产物；desc 精确匹配 vision issues
 * 剥除；reason = facts 引用 + 冲突判定）。
 */
export interface ResolvedClaimInfo {
  desc: string;
  severity?: 'hard' | 'soft';
  object?: string;
  reason?: string;
}

/** grounding 段脏数据防御（placement 段同范式）：非对象/resolved 非数组/缺 desc 条目丢弃，永不 crash。 */
function validResolvedClaims(raw: unknown): ResolvedClaimInfo[] {
  const arr = raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as { resolved?: unknown }).resolved
    : undefined;
  return Array.isArray(arr)
    ? arr.filter((r): r is ResolvedClaimInfo =>
        !!r && typeof r === 'object'
        && typeof (r as ResolvedClaimInfo).desc === 'string' && (r as ResolvedClaimInfo).desc !== '')
    : [];
}

/**
 * M3-1 仲裁纯函数：汇总 /api/critique 响应携带的确定性摆位审计段（placement，客观数据）与
 * vision verdict — det hard/soft 前置于 issues（refine 指令 hard-first 排序内部稳定，
 * residual/frozen/回归守卫管线直接复用），pass = vision pass 且 det hard 为空。
 * 旧响应无 placement 键 → 原样返回（向后兼容零回归）；脏数据（非数组/缺 desc/severity 非法）
 * 防御性丢弃，永不 crash。
 * SPEC-462 M3: grounding 段（可测量主张 vs 区域几何 facts 的确定性仲裁）— resolve 的主张按
 * desc 精确匹配从 vision issues 剥除（det 段 = 引擎实测产物，不参与 grounding）；冲突 hard
 * 主张全部 resolve 导致的 vision fail → pass 翻转（E4 三轮冻结根治；det hard 仍不可被覆盖 —
 * M3-1 语义不变）。无 grounding 键 → 旧行为逐字节不变（CRITIQUE_GROUNDING kill-switch 天然
 * worker 侧生效）。
 */
export function arbitrateCritique(verdict: CritiqueResult): { pass: boolean; issues: CritiqueIssue[]; resolved: ResolvedClaimInfo[] } {
  const raw = (verdict as CritiqueResult & { placement?: unknown }).placement;
  const det: PlacementIssue[] = Array.isArray(raw)
    ? raw.filter((i): i is PlacementIssue =>
        !!i && typeof i === 'object'
        && typeof (i as PlacementIssue).desc === 'string' && (i as PlacementIssue).desc !== ''
        && ((i as PlacementIssue).severity === 'hard' || (i as PlacementIssue).severity === 'soft'))
    : [];
  const detHard = det.filter(i => i.severity === 'hard');
  // SPEC-462 M3: grounding 段收口（无键/脏数据 → resolved=[] → 下方全部旧路径）。
  const resolved = validResolvedClaims((verdict as CritiqueResult & { grounding?: unknown }).grounding);
  let visionIssues = verdict.issues;
  let removedHard = 0;
  if (resolved.length > 0) {
    const resolvedDescs = new Set(resolved.map(r => r.desc));
    const kept: CritiqueIssue[] = [];
    for (const i of verdict.issues) {
      if (resolvedDescs.has(i.desc)) { if (i.severity === 'hard') removedHard++; } else kept.push(i);
    }
    if (kept.length !== verdict.issues.length) visionIssues = kept; // 零命中 → 原引用不动（byte-identical）
  }
  let pass = verdict.pass && detHard.length === 0;
  // 仲裁翻转：vision fail 的 hard 依据全部被 facts 冲突 resolve → pass（仅 hard 触发；
  // det hard 在场仍 fail — 确定性 hard 不可被覆盖，SPEC-460 M3-1 语义不变）。
  if (removedHard > 0 && detHard.length === 0 && !visionIssues.some(i => i.severity === 'hard')) pass = true;
  return {
    pass,
    issues: det.length > 0 ? [...det, ...visionIssues] : visionIssues,
    resolved,
  };
}

// ===== SPEC-461 M3.2（RC3 根治）— 异步落地补审触发判定 =====

/** 末次审查档案（runReviewLoop done 气泡落盘时写入；reaudited = 一次性守卫位）。 */
export interface LastReviewRecord {
  projectId: string;
  pageId: string;
  /** 末次 critique 的 instruction（补审 postCritique 复用）。 */
  instruction: string;
  hasReference?: boolean;
  reaudited: boolean;
}

/**
 * M3.2 触发判定纯函数：JOB_UPDATE 在末次 critique 之后落地（= review loop 不在飞、
 * 档案匹配当前项目/页、未补审过）→ true = 跑一次性终局 screenshot+critique+verdict
 *（不新开修复预算；fail → chat.loopUnresolved 诚实文案，不开新修复轮）。
 * 一次性（reaudited 置位后恒 false）= 防无限尾巴；loop 在飞 = loop 自己的 settle→critique
 * 会覆盖该落地，不补审。取证（doc-pmurx39zf1kfk）：msg2/msg5 异步入队、JOB_UPDATE 13:07
 * 落地 = 末次审查（13:01-13:03）之后 —— 终态存档从未过 screenshot+critique。
 */
export function shouldFinalReaudit(input: {
  jobStatus: string;
  loopActive: boolean;
  lastReview: LastReviewRecord | null;
  current: { projectId: string | null; pageId: string | null };
}): boolean {
  if (input.jobStatus !== 'done' && input.jobStatus !== 'error') return false;
  if (input.loopActive) return false;
  const lr = input.lastReview;
  if (!lr || lr.reaudited) return false;
  return lr.projectId === input.current.projectId && lr.pageId === input.current.pageId;
}

// ===== loop timing / hygiene constants =====

/** Wait cap for image jobs to settle before a screenshot (pollJob itself caps at 400s). */
export const JOBS_SETTLE_TIMEOUT_MS = 420_000;
/** Poll interval while waiting for jobs to settle. */
export const JOBS_SETTLE_POLL_MS = 1_000;
/** Pause after the last JOB_UPDATE so react-konva repaints the fresh pixels before capture. */
export const SETTLE_RENDER_DELAY_MS = 600;
/** Pause after clearing selection so the Transformer/outline removal commits before capture. */
export const SELECTION_SETTLE_MS = 150;
/** Critique screenshots are transient intermediates (only referenced by the review call) → TTL them (SPEC-388 precedent). */
export const SCREENSHOT_TTL_DAYS = 3;
/** Worker's buildFactSummary emits this when a turn changed nothing; the done message drops it. */
export const NO_CHANGE_SUMMARY = '无画布变更';

export function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}
